/**
 * THE TWO NAMED SURFACES. 09 §"PLATFORM and TENANT never bleed".
 *
 * `Platform Console` (קונסולת הפלטפורמה) and `Tenant Workspace` (סביבת הארגון) are different places.
 * 04 I6: exactly one context is active per session, and it is a property of the SESSION — never of
 * the user, never of the request. So the badge does not describe a mode the user can toggle
 * mid-screen; it names where the whole application currently is, and the chrome changes with it
 * (see `--surface-accent` in styles.css, switched by `data-surface` on the shell root).
 *
 * 09 §9.5 A3: colour is never the only carrier of meaning — the surface is written out in words.
 */

import type { SessionContext } from '../api/contract';
import { useLocale } from '../i18n/LocaleProvider';
import type { TextKey } from '../i18n/catalogue';

export interface SurfaceBadgeProps {
  /** `null` until `auth.whoami.read` answers. CM-014: never defaulted — see `ContextProvider`. */
  readonly surface: SessionContext | null;
  /** Non-null ⇒ a PLATFORM principal is inside a tenant under a support grant (04 §13.1.5). */
  readonly supportGrantUid: string | null;
}

/*
 * THE BADGE IS CHROME, AND CHROME FOLLOWS THE LANGUAGE. **Owner, 2026-10-23 §6.**
 *
 * The two surface names were Hebrew constants, so an English session named the place it was in,
 * in Hebrew, beside a navigation rail that had been translated. The Hebrew is unchanged — it moved
 * to the catalogue, where every string the product shows already lives, and acquired its English.
 */
const SURFACE_KEY: Readonly<Record<SessionContext, TextKey>> = {
  PLATFORM: 'shell.platformConsole',
  TENANT: 'surface.tenant',
};

export function SurfaceBadge({ surface, supportGrantUid }: SurfaceBadgeProps): JSX.Element {
  const { t } = useLocale();
  return (
    <span className="surface-badge" data-surface={surface ?? 'UNREAD'}>
      <span className="surface-badge__dot" aria-hidden="true" />
      {/* Said in words, because a badge with no surface must not look like a surface. */}
      <span className="surface-badge__label">
        {surface === null ? t('surface.unread') : t(SURFACE_KEY[surface])}
      </span>
      {supportGrantUid === null ? null : (
        // A support grant is the only bridge between PLATFORM and a tenant's data, and every call
        // under it is audited ALWAYS. The user must never be unsure that they are inside one.
        <span className="surface-badge__grant">{t('surface.supportGrant')}</span>
      )}
    </span>
  );
}
